import '@beyondtrust/btux-iconography-web';
import '@shoelace-style/shoelace/dist/components/dropdown/dropdown.js';
import { localized, msg } from '@lit/localize';
import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import './breadcrumb-item';
import { BREADCRUMB_ITEM_TAG } from './breadcrumb-item';
import styles from './breadcrumbs.styles.scss';

export const BREADCRUMBS_TAG = 'platform-breadcrumbs' as const;

/**
 * Breadcrumb trail. Give it `<platform-breadcrumb-item>` children; it draws the
 * chevron separators, collapses to `first › ••• › last two` past three crumbs
 * (the ••• opens a dropdown of the hidden ancestors), and exposes the navigation
 * landmark. Used by the sidebar header (slotted or inferred) and standalone.
 *
 * @public
 */
@customElement(BREADCRUMBS_TAG)
@localized()
export class PlatformBreadcrumbs extends LitElement {
  static override styles = [styles];

  /** Accessible name for the landmark; defaults to a localized "Breadcrumb". @public */
  @property() label = '';

  /** Accessible name for the overflow (•••) button; defaults to a localized string. @public */
  @property({ attribute: 'more-label' }) moreLabel = '';

  @state() private _overflowing = false;

  private _observer = new MutationObserver(() => this._assign());

  override connectedCallback() {
    super.connectedCallback();
    this._observer.observe(this, { childList: true });
    this._assign();
  }

  override disconnectedCallback() {
    this._observer.disconnect();
    super.disconnectedCallback();
  }

  override firstUpdated() {
    this._assign();
  }

  /** Route each crumb into the lead / overflow / tail slot for the fixed collapse rule. */
  private _assign() {
    const items = [...this.children].filter(
      (el): el is HTMLElement => el instanceof HTMLElement && el.localName === BREADCRUMB_ITEM_TAG,
    );
    const overflowing = items.length > 3;
    const last = items.length - 1;
    items.forEach((item, i) => {
      const isLead = i === 0;
      // Everything between the first crumb and the final two collapses.
      const inOverflow = overflowing && i >= 1 && i < last - 1;
      item.slot = isLead ? 'lead' : inOverflow ? 'overflow' : 'tail';
      item.toggleAttribute('leading', isLead);
      item.toggleAttribute('in-overflow', inOverflow);
    });
    this._overflowing = overflowing;
  }

  override render() {
    return html`
      <nav
        aria-label=${this.label ||
        msg('Breadcrumb', { id: 'breadcrumbNavLabel', desc: 'Accessible name for the breadcrumb navigation landmark' })}
      >
        <div class="list" role="list" part="list">
          <slot name="lead"></slot>
          ${this._overflowing
            ? html`
                <span class="overflow-item" role="listitem">
                  <btux-svg class="separator" name="chevron-right" aria-hidden="true"></btux-svg>
                  <sl-dropdown class="overflow" distance="4" hoist>
                    <button
                      type="button"
                      slot="trigger"
                      part="overflow-trigger"
                      class="overflow-trigger"
                      aria-label=${this.moreLabel ||
                      msg('Show more breadcrumbs', {
                        id: 'breadcrumbOverflowLabel',
                        desc: 'Accessible name for the button that reveals the collapsed breadcrumbs',
                      })}
                    >
                      <svg class="ellipsis" viewBox="0 0 24 24" aria-hidden="true" fill="currentColor">
                        <circle cx="5" cy="12" r="2"></circle>
                        <circle cx="12" cy="12" r="2"></circle>
                        <circle cx="19" cy="12" r="2"></circle>
                      </svg>
                    </button>
                    <div class="overflow-panel" part="overflow-panel" role="list">
                      <slot name="overflow"></slot>
                    </div>
                  </sl-dropdown>
                </span>
              `
            : nothing}
          <slot name="tail"></slot>
        </div>
      </nav>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [BREADCRUMBS_TAG]: PlatformBreadcrumbs;
  }
}
